import React from 'react';
import { Card, Descriptions, Tag, Space, Input, Select, Form, Button } from 'antd';
import { NumberOutlined, CalendarOutlined, ClockCircleOutlined, EditOutlined, SaveOutlined, CloseOutlined, MobileOutlined, TabletOutlined } from '@ant-design/icons';

interface Device {
  id: string;
  device_id: string;
  imei: string;
  name: string;
  device_type: string;
  tags?: string[];
  operation_mode: 'batch' | 'realtime';
  last_seen_at?: string;
  created_at: string;
}

interface DeviceBasicInfoProps {
  device: Device;
  isEditing: boolean;
  form: any;
  onEditModeChange?: (editing: boolean) => void;
  onSave?: () => void;
}

const DeviceBasicInfo: React.FC<DeviceBasicInfoProps> = ({ device, isEditing, form, onEditModeChange, onSave }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  return (
    <Card 
      title={<span style={{ fontSize: '12px' }}>基本情報</span>} 
      size="small"
      extra={
        !isEditing ? (
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            style={{ fontSize: '12px' }}
            onClick={() => {
              form.setFieldsValue({
                name: device.name,
                tags: device.tags || []
              });
              onEditModeChange?.(true);
            }}
          >
            編集
          </Button>
        ) : (
          <Space>
            <Button 
              size="small"
              icon={<CloseOutlined />}
              style={{ fontSize: '12px' }}
              onClick={() => {
                form.resetFields();
                onEditModeChange?.(false);
              }}
            >
              キャンセル
            </Button>
            <Button 
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              style={{ fontSize: '12px' }}
              onClick={onSave}
            >
              保存
            </Button>
          </Space>
        )
      }
    >
      {!isEditing ? (
        <Descriptions column={1} size="small">
          <Descriptions.Item 
            label={
              <Space>
                <TabletOutlined />
                デバイスタイプ
              </Space>
            }
          >
            {device.device_type || '不明'}
          </Descriptions.Item>
          <Descriptions.Item 
            label={
              <Space>
                <NumberOutlined />
                IMEI番号
              </Space>
            }
          >
            <code>{device.imei}</code>
          </Descriptions.Item>
          <Descriptions.Item 
            label={
              <Space>
                <MobileOutlined />
                デバイス名
              </Space>
            }
          >
            {device.name}
          </Descriptions.Item>
          <Descriptions.Item 
            label={
              <Space>
                <CalendarOutlined />
                登録日時
              </Space>
            }
          >
            {formatDate(device.created_at)}
          </Descriptions.Item>

          {device.tags && device.tags.length > 0 && (
            <Descriptions.Item label="タグ">
              <Space wrap>
                {device.tags.map(tag => (
                  <Tag key={tag} color="blue">{tag}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      ) : (
        <Form form={form} layout="vertical">
          <Descriptions column={1} size="small">
            <Descriptions.Item 
              label={
                <Space>
                  <TabletOutlined />
                  デバイスタイプ
                </Space>
              }
            >
              {device.device_type || '不明'}
              <span style={{ marginLeft: 8, color: '#999', fontSize: '12px' }}>(変更不可)</span>
            </Descriptions.Item>
            <Descriptions.Item 
              label={
                <Space>
                  <NumberOutlined />
                  IMEI番号
                </Space>
              }
            >
              <code>{device.imei}</code>
              <span style={{ marginLeft: 8, color: '#999', fontSize: '12px' }}>(変更不可)</span>
            </Descriptions.Item>
            <Descriptions.Item 
              label={
                <Space>
                  <MobileOutlined />
                  デバイス名
                </Space>
              }
            >
              <Form.Item 
                name="name" 
                style={{ margin: 0 }}
                rules={[
                  { required: true, message: 'デバイス名を入力してください' },
                  { max: 50, message: '50文字以内で入力してください' }
                ]}
              >
                <Input placeholder="デバイス名を入力" style={{ fontSize: '12px' }} />
              </Form.Item>
            </Descriptions.Item>
            <Descriptions.Item 
              label={
                <Space>
                  <CalendarOutlined />
                  登録日時
                </Space>
              }
            >
              {formatDate(device.created_at)}
            </Descriptions.Item>

            <Descriptions.Item label="タグ">
              <Form.Item name="tags" style={{ margin: 0 }}>
                <Select
                  mode="tags"
                  placeholder="タグを選択または入力"
                  style={{ minWidth: 200 }}
                  options={[
                    { label: 'メイン', value: 'メイン' },
                    { label: '会議用', value: '会議用' },
                    { label: '外出用', value: '外出用' },
                    { label: 'テスト用', value: 'テスト用' },
                    { label: '自宅用', value: '自宅用' },
                    { label: 'オフィス用', value: 'オフィス用' },
                  ]}
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <Tag
                        color="blue"
                        closable={closable}
                        onClose={onClose}
                        style={{ marginRight: 3 }}
                      >
                        {label}
                      </Tag>
                    );
                  }}
                />
              </Form.Item>
            </Descriptions.Item>
          </Descriptions>
        </Form>
      )}
    </Card>
  );
};

export default DeviceBasicInfo;